import React, { ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import RegisterForm from '@/common/auth/components/forms/RegisterForm';
import { useAuth } from '@/common/auth/hooks/useAuth';
import { LogoMark, useModeTokens } from '@/common/components/ui';

/**
 * A plain centred card, not the landing page.
 *
 * `/register` is the last step of signing in — an authenticated user without an
 * artist name gets sent here — so it is an onboarding form, not a place to be
 * sold the product a second time.
 */
const AuthCard: React.FC<{ children: ReactNode }> = ({ children }) => {
  const t = useModeTokens();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, sm: 3 },
        backgroundColor: t.surfaces.canvas,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          border: `1px solid ${t.borders.medium}`,
          borderRadius: '10px',
          background: t.surfaces.panel,
          boxShadow: t.shadows.modal,
          padding: { xs: 3, sm: 4 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <LogoMark size={26} />
        </Box>
        {children}
      </Box>
    </Box>
  );
};

const RegisterPage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  // Show loading while checking authentication status
  if (isAuthenticated === undefined) {
    return (
      <AuthCard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '240px',
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <RegisterForm />
    </AuthCard>
  );
};

export default RegisterPage;
